import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Checkbox,
  TextField,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiX,
} from "react-icons/fi";
import TagDialog from "../components/TagDialog";
import TableLoader from "../components/TableLoader";
import { colors } from "../theme/modernTheme";
import { useMultiSelect } from "../hooks/useMultiSelect";
import { useDebounce } from "../hooks/useDebounce";
import BulkActionToolbar from "../components/BulkActionToolbar";
import BulkDeleteDialog from "../components/BulkDeleteDialog";
import { showSuccess, showError } from "../utils/toast";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../context/AuthContext";
import {
  deleteTag,
  bulkDeleteTags,
  fetchTags,
  selectAllTags,
  selectTagsTotal,
  selectTagsStatus,
} from "../store/slices/tagsSlice";
import { MODULE_PERMISSIONS } from "../utils/modulePermissions";

export default function Tags() {
  const { hasWritePermission } = useAuth();
  const dispatch = useDispatch();
  const tags = useSelector(selectAllTags);
  const totalCount = useSelector(selectTagsTotal);
  const status = useSelector(selectTagsStatus);
  const loading = status === "loading";

  // RBAC: Check if user has write permission for tags
  const canWrite = hasWritePermission(MODULE_PERMISSIONS.TAGS);
  const canDelete = canWrite; // Delete requires write permission

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);

  // Search and pagination states
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 500);
  const [page, setPage] = useState(0);
  const rowsPerPage = 12;
  const [sortBy] = useState("name");
  const [sortOrder] = useState("asc");

  // Multi-select state
  const multiSelect = useMultiSelect(tags, totalCount, "id");
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Load tags when params change
  useEffect(() => {
    loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, page, sortBy, sortOrder]);

  // Reset to page 0 when search changes
  useEffect(() => {
    if (page !== 0) {
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const loadTags = () => {
    const params = {
      search: debouncedSearch,
      page: page + 1,
      limit: rowsPerPage,
      sortBy: sortBy,
      sortOrder: sortOrder,
    };
    dispatch(fetchTags(params));
  };

  const handleAdd = () => {
    setSelectedTag(null);
    setDialogOpen(true);
  };

  const handleEdit = (tag) => {
    setSelectedTag(tag);
    setDialogOpen(true);
  };

  const handleDeleteClick = (tag) => {
    setTagToDelete(tag);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteTag(tagToDelete.id)).unwrap();
      multiSelect.removeFromSelection(tagToDelete.id);
      setDeleteDialogOpen(false);
      setTagToDelete(null);
      // After delete, we should reload the current page to fill empty spots or handle empty pages
      loadTags();
    } catch (error) {
      showError("Failed to delete tag");
    }
  };

  const handleSuccess = () => {
    // Reload to show new/updated tag
    loadTags();
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setPage(0);
    multiSelect.clearSelection();
  };

  const handlePreviousPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(totalCount / rowsPerPage);
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const selectionData = multiSelect.getSelectionData();
      const filters = {};
      if (debouncedSearch) filters.search = debouncedSearch;

      await dispatch(
        bulkDeleteTags({
          ...selectionData,
          filters,
        })
      ).unwrap();

      setBulkDeleteDialogOpen(false);
      multiSelect.clearSelection();
      loadTags();
      showSuccess(`Successfully deleted ${multiSelect.selectedCount} tag(s)`);
    } catch (error) {
      showError(error?.message || "Failed to delete tags");
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={1}
        mb={1}>
        <Typography
          variant="h4"
          sx={{
            fontSize: "1.5rem",
            fontWeight: 700,
            color: colors.text.primary,
          }}>
          Tags
        </Typography>
        {canWrite && (
          <Button
            variant="contained"
            startIcon={<FiPlus />}
            onClick={handleAdd}
            sx={{
              borderRadius: "4px",
              backgroundColor: colors.primary.main,
              "&:hover": { backgroundColor: colors.primary.dark },
            }}>
            Create Tag
          </Button>
        )}
      </Box>

      {/* Search and Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "4px",
          boxShadow: "none",
          opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
          pointerEvents: multiSelect.selectedCount > 0 ? "none" : "auto",
        }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tags..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              disabled={multiSelect.selectedCount > 0}
              InputProps={{
                endAdornment: searchInput ? (
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    disabled={multiSelect.selectedCount > 0}
                    sx={{ padding: "4px" }}>
                    <FiX size={16} style={{ color: colors.text.secondary }} />
                  </IconButton>
                ) : (
                  <FiSearch
                    style={{ marginRight: 8, color: colors.text.secondary }}
                  />
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.background.paper,
                  borderRadius: "4px",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
              }}>
              {searchInput && (
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  disabled={multiSelect.selectedCount > 0}
                  sx={{
                    backgroundColor: "transparent",
                    color: colors.text.secondary,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}>
                  Clear
                </Button>
              )}
              <IconButton
                size="small"
                onClick={handlePreviousPage}
                disabled={page === 0 || multiSelect.selectedCount > 0}
                sx={{
                  marginRight: -2,
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: colors.action.hover,
                  },
                }}>
                <FiChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  minWidth: "100px",
                  textAlign: "center",
                }}>
                Page {page + 1} of {Math.ceil(totalCount / rowsPerPage) || 1}
              </Typography>
              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={
                  page >= Math.ceil(totalCount / rowsPerPage) - 1 ||
                  multiSelect.selectedCount > 0
                }
                sx={{
                  marginLeft: -2,
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: colors.action.hover,
                  },
                }}>
                <FiChevronRight />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={multiSelect.selectedCount}
        onClearSelection={multiSelect.clearSelection}
        onDelete={() => setBulkDeleteDialogOpen(true)}
        canDelete={canDelete}
        itemName="tags"
        showSelectAllPage={true}
        onSelectAllPage={multiSelect.toggleSelectAll}
        isAllPageSelected={multiSelect.isAllCurrentPageSelected}
      />

      {loading ? (
        <TableLoader message="Loading tags..." />
      ) : tags.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            color: colors.text.secondary,
          }}>
          <Typography variant="body1">No tags found</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {tags.map((tag) => (
            <Grid item xs={12} sm={6} md={4} key={tag.id}>
              <Card
                sx={{
                  position: "relative",
                  borderRadius: "4px",
                  boxShadow: "none",
                  border: `1px solid ${colors.border.main}`,
                  transition: "all 0.1s ease-in-out",
                  backgroundColor: multiSelect.isSelected(tag)
                    ? colors.primary.main + "10"
                    : "transparent",
                  "&:hover .tag-actions": {
                    opacity: 1,
                  },
                }}>
                <CardContent sx={{ backgroundColor: colors.background.paper }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Checkbox
                        size="medium"
                        checked={multiSelect.isSelected(tag)}
                        onChange={(e) => {
                          e.stopPropagation();
                          multiSelect.toggleSelect(tag);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          padding: "4px",
                          "& .MuiSvgIcon-root": { fontSize: 28 },
                          transform: "scale(1.2)",
                          color: colors.text.secondary,
                          "&.Mui-checked": {
                            color: colors.primary.main,
                          },
                        }}
                      />
                      <Chip
                        label={tag.name}
                        sx={{
                          backgroundColor: tag.color
                            ? `${tag.color}15`
                            : `${colors.primary.main}15`,
                          color: tag.color || colors.primary.main,
                          fontWeight: 600,
                          borderRadius: "4px",
                          fontSize: "0.875rem",
                        }}
                      />
                    </Box>
                    {multiSelect.selectedCount > 0
                      ? null
                      : canWrite && (
                          <Box
                            className="tag-actions"
                            sx={{
                              opacity: 0,
                              transition: "opacity 0.2s",
                              display: "flex",
                              gap: 0.5,
                            }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(tag)}
                              sx={{
                                color: colors.text.secondary,
                                "&:hover": {
                                  color: colors.primary.main,
                                  backgroundColor: "transparent",
                                },
                              }}>
                              <FiEdit2 size={16} />
                            </IconButton>
                            {canDelete && (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(tag)}
                                sx={{
                                  color: colors.text.secondary,
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: colors.error.main,
                                  },
                                }}>
                                <FiTrash2 size={16} />
                              </IconButton>
                            )}
                          </Box>
                        )}
                  </Box>
                  <Tooltip
                    title={tag.description || "No description"}
                    arrow
                    placement="bottom">
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        color: colors.text.secondary,
                        mt: 1,
                        ml: "35px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        lineHeight: "1.4em",
                        height: "2.8em",
                      }}>
                      {tag.description || "No description"}
                    </Typography>
                  </Tooltip>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Bottom Pagination */}
      {!loading && tags.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 2,
            gap: 1,
          }}>
          <IconButton
            size="small"
            onClick={handlePreviousPage}
            disabled={page === 0}
            sx={{
              marginRight: -2,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: colors.action.hover,
              },
              "&:disabled": {
                opacity: 0.5,
                cursor: "not-allowed",
              },
            }}>
            <FiChevronLeft />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              minWidth: "100px",
              textAlign: "center",
            }}>
            Page {page + 1} of {Math.ceil(totalCount / rowsPerPage) || 1}
          </Typography>
          <IconButton
            size="small"
            onClick={handleNextPage}
            disabled={page >= Math.ceil(totalCount / rowsPerPage) - 1}
            sx={{
              marginLeft: -2,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: colors.action.hover,
              },
              "&:disabled": {
                opacity: 0.5,
                cursor: "not-allowed",
              },
            }}>
            <FiChevronRight />
          </IconButton>
        </Box>
      )}

      <TagDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        tag={selectedTag}
        onSuccess={handleSuccess}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName="tag"
        message={`Are you sure you want to delete the tag "${tagToDelete?.name}"? This action cannot be undone.`}
        deleting={loading}
      />

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        count={multiSelect.selectedCount}
        itemName="tags"
        deleting={bulkDeleting}
      />
    </Box>
  );
}
